import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import {
  Search, Loader2, Receipt, CheckCircle, Clock, AlertTriangle, Filter,
  CreditCard, Banknote, Download, FileText, ChevronDown, ChevronUp,
  Package, BookOpen, PartyPopper, Bell, BellRing, TrendingUp,
  Home, Zap, Droplets, Wifi, Users, Wrench, ShoppingBag, MoreHorizontal,
  Minus, DollarSign, RefreshCw, CalendarClock, UserCheck, Percent,
  Eye, XCircle, Image,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { format, subMonths, startOfMonth, endOfMonth, getDaysInMonth } from "date-fns";
import { ar } from "date-fns/locale";
import { toast } from "sonner";
import AddInvoiceDialog from "@/components/dialogs/AddInvoiceDialog";
import AddFeeTemplateDialog from "@/components/dialogs/AddFeeTemplateDialog";
import AddExpenseDialog, { EXPENSE_CATEGORIES } from "@/components/dialogs/AddExpenseDialog";
import DeleteConfirmDialog from "@/components/dialogs/DeleteConfirmDialog";
import { exportToCSV } from "@/lib/export-utils";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const CHART_COLORS = {
  paid: "hsl(142 71% 45%)",
  pending: "hsl(48 96% 53%)",
  overdue: "hsl(0 84% 60%)",
};

const CATEGORY_CONFIG: Record<string, { label: string; icon: any; color: string }> = {
  rent: { label: "إيجار", icon: Home, color: "text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400" },
  electricity: { label: "كهرباء", icon: Zap, color: "text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400" },
  water: { label: "مياه", icon: Droplets, color: "text-cyan-600 bg-cyan-100 dark:bg-cyan-900/30 dark:text-cyan-400" },
  internet: { label: "إنترنت", icon: Wifi, color: "text-purple-600 bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400" },
  salaries: { label: "رواتب", icon: Users, color: "text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400" },
  supplies: { label: "مستلزمات", icon: ShoppingBag, color: "text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400" },
  maintenance: { label: "صيانة", icon: Wrench, color: "text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400" },
  other: { label: "أخرى", icon: MoreHorizontal, color: "text-muted-foreground bg-muted" },
};

const Billing = () => {
  const { profile, user } = useAuth();
  const nurseryId = profile?.nursery_id;
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [classFilter, setClassFilter] = useState("all");
  const [expandedInvoice, setExpandedInvoice] = useState<string | null>(null);
  const [sendingAlert, setSendingAlert] = useState<string | null>(null);
  const [sendingBulk, setSendingBulk] = useState(false);
  const [expenseCategoryFilter, setExpenseCategoryFilter] = useState("all");
  const [mainTab, setMainTab] = useState("invoices");
  const [generatingInvoices, setGeneratingInvoices] = useState(false);
  const [reviewingInvoice, setReviewingInvoice] = useState<any | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [receiptPreviewUrl, setReceiptPreviewUrl] = useState<string | null>(null);

  const { data: children } = useQuery({
    queryKey: ["billing-children", nurseryId],
    queryFn: async () => {
      const { data } = await supabase.from("children").select("id, full_name, parent_name, parent_id, class_id, monthly_fee, enrollment_date").eq("nursery_id", nurseryId!);
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const { data: classes } = useQuery({
    queryKey: ["billing-classes", nurseryId],
    queryFn: async () => {
      const { data } = await supabase.from("classes").select("id, name").eq("nursery_id", nurseryId!).order("name");
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const { data: invoices, isLoading, refetch } = useQuery({
    queryKey: ["invoices", nurseryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("*, children(full_name, parent_name, parent_id, class_id)")
        .eq("nursery_id", nurseryId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const { data: invoiceItems } = useQuery({
    queryKey: ["invoice-items", nurseryId],
    queryFn: async () => {
      if (!invoices?.length) return [];
      const ids = invoices.map((i: any) => i.id);
      const { data } = await supabase.from("invoice_items").select("*").in("invoice_id", ids);
      return data || [];
    },
    enabled: !!invoices?.length,
  });

  const { data: feeTemplates, refetch: refetchTemplates } = useQuery({
    queryKey: ["fee-templates", nurseryId],
    queryFn: async () => {
      const { data } = await supabase.from("fee_templates").select("*").eq("nursery_id", nurseryId!).order("name");
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const { data: expenses, isLoading: expensesLoading, refetch: refetchExpenses } = useQuery({
    queryKey: ["expenses", nurseryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("expenses")
        .select("*")
        .eq("nursery_id", nurseryId!)
        .order("expense_date", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const markAsPaid = async (id: string, method: string) => {
    const { error } = await supabase
      .from("invoices")
      .update({ status: "paid", paid_at: new Date().toISOString(), payment_method: method })
      .eq("id", id);
    if (error) toast.error(error.message);
    else { toast.success("تم تسجيل الدفع"); refetch(); }
  };

  const sendAlert = async (inv: any) => {
    const parentId = inv.children?.parent_id;
    if (!parentId || !nurseryId) {
      toast.error("لا يمكن إرسال تنبيه - ولي الأمر غير مرتبط");
      return;
    }
    setSendingAlert(inv.id);
    const { error } = await supabase.from("notifications").insert({
      nursery_id: nurseryId,
      user_id: parentId,
      title: "⚠️ فاتورة متأخرة",
      body: `فاتورة "${inv.title}" بقيمة ${Number(inv.amount).toLocaleString()} ج.م متأخرة عن موعد الاستحقاق. يرجى السداد في أقرب وقت.`,
      type: "overdue_invoice",
      reference_id: inv.id,
    });
    setSendingAlert(null);
    if (error) toast.error("خطأ في إرسال التنبيه");
    else toast.success(`تم إرسال تنبيه لولي أمر ${inv.children?.full_name}`);
  };

  const sendBulkAlerts = async () => {
    if (!nurseryId) return;
    const overdueInvs = (invoices || []).filter((i: any) => i.status === "overdue" && i.children?.parent_id);
    if (!overdueInvs.length) { toast.info("لا توجد فواتير متأخرة لإرسال تنبيهات"); return; }
    setSendingBulk(true);
    const notifications = overdueInvs.map((inv: any) => ({
      nursery_id: nurseryId,
      user_id: inv.children.parent_id,
      title: "⚠️ فاتورة متأخرة",
      body: `فاتورة "${inv.title}" بقيمة ${Number(inv.amount).toLocaleString()} ج.م متأخرة. يرجى السداد.`,
      type: "overdue_invoice",
      reference_id: inv.id,
    }));
    const { error } = await supabase.from("notifications").insert(notifications);
    setSendingBulk(false);
    if (error) toast.error("خطأ في إرسال التنبيهات");
    else toast.success(`تم إرسال ${notifications.length} تنبيه بنجاح`);
  };

  const deleteExpense = async (id: string) => {
    const { error } = await supabase.from("expenses").delete().eq("id", id);
    if (error) toast.error("خطأ في حذف المصروف");
    else { toast.success("تم حذف المصروف"); refetchExpenses(); }
  };

  const generateMonthlyInvoices = async () => {
    if (!nurseryId) return;
    setGeneratingInvoices(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-monthly-invoices", {
        body: { nursery_id: nurseryId },
      });
      if (error) throw error;
      toast.success(`تم توليد ${data?.generated || 0} فاتورة بنجاح`);
      refetch();
    } catch (err: any) {
      toast.error("خطأ في توليد الفواتير: " + (err.message || "خطأ غير معروف"));
    }
    setGeneratingInvoices(false);
  };

  const approvePayment = async (inv: any) => {
    setReviewSubmitting(true);
    const { error } = await supabase
      .from("invoices")
      .update({
        status: "paid",
        paid_at: new Date().toISOString(),
        reviewed_by: user?.id,
        reviewed_at: new Date().toISOString(),
      })
      .eq("id", inv.id);
    setReviewSubmitting(false);
    if (error) toast.error("خطأ في تأكيد الدفع");
    else { toast.success("تم تأكيد الدفع بنجاح"); refetch(); setReviewingInvoice(null); }
  };

  const rejectPayment = async () => {
    if (!reviewingInvoice || !rejectionReason.trim()) {
      toast.error("يرجى كتابة سبب الرفض");
      return;
    }
    setReviewSubmitting(true);
    const { error } = await supabase
      .from("invoices")
      .update({
        status: "rejected",
        rejection_reason: rejectionReason.trim(),
        reviewed_by: user?.id,
        reviewed_at: new Date().toISOString(),
      })
      .eq("id", reviewingInvoice.id);
    setReviewSubmitting(false);
    if (error) toast.error("خطأ في رفض الدفع");
    else { toast.success("تم رفض الدفع"); refetch(); setReviewingInvoice(null); setRejectionReason(""); }
  };

  const openReceiptPreview = async (receiptUrl: string) => {
    const { data } = await supabase.storage.from("payment-receipts").createSignedUrl(receiptUrl, 300);
    if (data?.signedUrl) setReceiptPreviewUrl(data.signedUrl);
    else toast.error("فشل في تحميل الإيصال");
  };

  const pendingReviewInvoices = (invoices || []).filter((i: any) => i.status === "under_review" || i.status === "awaiting_confirmation");


  const filtered = (invoices || []).filter((inv: any) => {
    const matchSearch = inv.title?.includes(search) || inv.children?.full_name?.includes(search) || inv.children?.parent_name?.includes(search);
    const matchStatus = statusFilter === "all" || inv.status === statusFilter;
    const matchType = typeFilter === "all" || inv.fee_type === typeFilter;
    const matchClass = classFilter === "all" || inv.children?.class_id === classFilter;
    return matchSearch && matchStatus && matchType && matchClass;
  });

  const monthlyInvoices = filtered.filter((i: any) => i.fee_type === "monthly");
  const additionalInvoices = filtered.filter((i: any) => i.fee_type === "additional");

  const allInvoices = invoices || [];
  const totalPending = allInvoices.filter((i: any) => i.status === "pending").reduce((s: number, i: any) => s + Number(i.amount), 0);
  const totalPaid = allInvoices.filter((i: any) => i.status === "paid").reduce((s: number, i: any) => s + Number(i.amount), 0);
  const totalOverdue = allInvoices.filter((i: any) => i.status === "overdue").reduce((s: number, i: any) => s + Number(i.amount), 0);
  const totalAll = totalPending + totalPaid + totalOverdue;
  const collectionRate = totalAll > 0 ? Math.round((totalPaid / totalAll) * 100) : 0;
  const overdueCount = allInvoices.filter((i: any) => i.status === "overdue").length;

  // Subscription stats
  const childrenWithFees = (children || []).filter((c: any) => Number(c.monthly_fee) > 0);
  const currentMonthInvoices = allInvoices.filter((i: any) => {
    const d = new Date(i.due_date);
    const n = new Date();
    return i.fee_type === "monthly" && d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear();
  });
  const paidThisMonth = currentMonthInvoices.filter((i: any) => i.status === "paid").length;
  const monthlyCollectionRate = currentMonthInvoices.length > 0 ? Math.round((paidThisMonth / currentMonthInvoices.length) * 100) : 0;

  const allExpenses = expenses || [];
  const totalExpenses = allExpenses.reduce((s: number, e: any) => s + Number(e.amount), 0);
  const currentMonthExpenses = allExpenses
    .filter((e: any) => {
      const d = new Date(e.expense_date);
      const now = new Date();
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    })
    .reduce((s: number, e: any) => s + Number(e.amount), 0);
  const netProfit = totalPaid - totalExpenses;

  const filteredExpenses = allExpenses.filter((e: any) =>
    expenseCategoryFilter === "all" || e.category === expenseCategoryFilter
  );

  // Chart data
  const monthlyChartData = useMemo(() => {
    const now = new Date();
    const months: { name: string; paid: number; pending: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = subMonths(now, i);
      const start = startOfMonth(d);
      const end = endOfMonth(d);
      const label = format(d, "MMM", { locale: ar });
      let paid = 0, pending = 0;
      allInvoices.forEach((inv: any) => {
        const created = new Date(inv.created_at);
        if (created >= start && created <= end) {
          if (inv.status === "paid") paid += Number(inv.amount);
          else pending += Number(inv.amount);
        }
      });
      months.push({ name: label, paid, pending });
    }
    return months;
  }, [allInvoices]);

  const pieData = useMemo(() => {
    const paid = allInvoices.filter((i: any) => i.status === "paid").length;
    const pending = allInvoices.filter((i: any) => i.status === "pending").length;
    const overdue = allInvoices.filter((i: any) => i.status === "overdue").length;
    return [
      { name: "مدفوع", value: paid, color: CHART_COLORS.paid },
      { name: "معلق", value: pending, color: CHART_COLORS.pending },
      { name: "متأخر", value: overdue, color: CHART_COLORS.overdue },
    ].filter(d => d.value > 0);
  }, [allInvoices]);

  const statusBadge = (status: string) => {
    switch (status) {
      case "paid": return <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"><CheckCircle className="w-3 h-3 ml-1" />مدفوع</Badge>;
      case "pending": return <Badge variant="secondary" className="gap-1"><Clock className="w-3 h-3" />معلق</Badge>;
      case "overdue": return <Badge variant="destructive" className="gap-1"><AlertTriangle className="w-3 h-3" />متأخر</Badge>;
      case "awaiting_confirmation": return <Badge className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 gap-1"><Clock className="w-3 h-3" />بانتظار التأكيد</Badge>;
      case "under_review": return <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 gap-1"><Eye className="w-3 h-3" />قيد المراجعة</Badge>;
      case "rejected": return <Badge variant="destructive" className="gap-1"><XCircle className="w-3 h-3" />مرفوض</Badge>;
      case "cancelled": return <Badge variant="outline">ملغي</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const feeTypeLabel = (t: string) => t === "monthly" ? "شهرية" : t === "additional" ? "إضافية" : t;

  const itemTypeIcon = (t: string) => {
    switch (t) {
      case "subscription": return <Package className="w-3.5 h-3.5 text-primary" />;
      case "educational": return <BookOpen className="w-3.5 h-3.5 text-blue-500" />;
      case "activity": return <PartyPopper className="w-3.5 h-3.5 text-orange-500" />;
      default: return <FileText className="w-3.5 h-3.5 text-muted-foreground" />;
    }
  };

  const itemTypeLabel = (t: string) => {
    switch (t) {
      case "subscription": return "اشتراك";
      case "educational": return "تعليمي";
      case "activity": return "نشاط";
      default: return "أخرى";
    }
  };

  const formatDate = (d: string | null) => {
    if (!d) return "-";
    try { return format(new Date(d), "d MMM yyyy", { locale: ar }); } catch { return d; }
  };

  const getItemsForInvoice = (invoiceId: string) => (invoiceItems || []).filter((item: any) => item.invoice_id === invoiceId);

  const handleExportCSV = () => {
    if (!filtered.length) return;
    const rows = filtered.map((inv: any) => ({
      "العنوان": inv.title,
      "الطفل": inv.children?.full_name || "",
      "ولي الأمر": inv.children?.parent_name || "",
      "المبلغ": inv.amount,
      "النوع": feeTypeLabel(inv.fee_type),
      "الحالة": inv.status === "paid" ? "مدفوع" : inv.status === "pending" ? "معلق" : "متأخر",
      "تاريخ الاستحقاق": inv.due_date,
      "تاريخ الدفع": inv.paid_at || "",
      "طريقة الدفع": inv.payment_method || "",
    }));
    exportToCSV(rows, `فواتير_${new Date().toISOString().split("T")[0]}.csv`);
    toast.success("تم تصدير الفواتير");
  };

  const handleExportExpensesCSV = () => {
    if (!filteredExpenses.length) return;
    const rows = filteredExpenses.map((e: any) => ({
      "العنوان": e.title,
      "الفئة": CATEGORY_CONFIG[e.category]?.label || e.category,
      "المبلغ": e.amount,
      "التاريخ": e.expense_date,
      "طريقة الدفع": e.payment_method || "",
      "متكرر": e.is_recurring ? "نعم" : "لا",
      "ملاحظات": e.notes || "",
    }));
    exportToCSV(rows, `مصروفات_${new Date().toISOString().split("T")[0]}.csv`);
    toast.success("تم تصدير المصروفات");
  };

  const handleDownloadPDF = async (inv: any) => {
    try {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");
      const items = getItemsForInvoice(inv.id);
      const itemsTotal = items.reduce((s: number, it: any) => s + Number(it.total || it.amount * it.quantity), 0);

      const container = document.createElement("div");
      container.style.cssText = "position:fixed;left:-9999px;top:0;width:794px;padding:40px;background:#fff;direction:rtl;font-family:'Segoe UI',Tahoma,Arial,sans-serif;color:#1a1a1a;";
      container.innerHTML = `
        <div style="text-align:center;margin-bottom:24px;">
          <h1 style="font-size:28px;font-weight:bold;margin:0;color:#1e40af;">فاتورة</h1>
          <p style="font-size:12px;color:#888;margin-top:4px;">رقم: ${inv.id.slice(0, 8)}</p>
        </div>
        <div style="display:flex;justify-content:space-between;margin-bottom:20px;font-size:13px;">
          <div>
            <p style="margin:4px 0;"><strong>الطفل:</strong> ${inv.children?.full_name || "-"}</p>
            <p style="margin:4px 0;"><strong>ولي الأمر:</strong> ${inv.children?.parent_name || "-"}</p>
          </div>
          <div style="text-align:left;">
            <p style="margin:4px 0;"><strong>تاريخ الاستحقاق:</strong> ${inv.due_date}</p>
            <p style="margin:4px 0;"><strong>الحالة:</strong> ${inv.status === "paid" ? "مدفوع" : inv.status === "pending" ? "معلق" : inv.status === "overdue" ? "متأخر" : inv.status}</p>
            ${inv.paid_at ? `<p style="margin:4px 0;"><strong>تاريخ الدفع:</strong> ${formatDate(inv.paid_at)}</p>` : ""}
            ${inv.payment_method ? `<p style="margin:4px 0;"><strong>طريقة الدفع:</strong> ${inv.payment_method}</p>` : ""}
          </div>
        </div>
        <table style="width:100%;border-collapse:collapse;font-size:13px;margin-bottom:16px;">
          <thead>
            <tr style="background:#1e40af;color:#fff;">
              <th style="padding:8px 12px;text-align:right;">البند</th>
              <th style="padding:8px 12px;text-align:center;">النوع</th>
              <th style="padding:8px 12px;text-align:center;">الكمية</th>
              <th style="padding:8px 12px;text-align:left;">المبلغ</th>
            </tr>
          </thead>
          <tbody>
            ${items.length > 0 ? items.map((it: any, idx: number) => `
              <tr style="background:${idx % 2 === 0 ? "#f9fafb" : "#fff"};">
                <td style="padding:6px 12px;">${it.description}</td>
                <td style="padding:6px 12px;text-align:center;">${it.item_type === "subscription" ? "اشتراك" : it.item_type === "educational" ? "تعليمي" : it.item_type === "activity" ? "نشاط" : "أخرى"}</td>
                <td style="padding:6px 12px;text-align:center;">${it.quantity}</td>
                <td style="padding:6px 12px;text-align:left;">${Number(it.total || it.amount * it.quantity).toLocaleString()} ج.م</td>
              </tr>
            `).join("") : `<tr><td colspan="4" style="padding:12px;text-align:center;color:#888;">${inv.title}</td></tr>`}
          </tbody>
        </table>
        <div style="text-align:left;font-size:18px;font-weight:bold;padding:12px;background:#f0f9ff;border-radius:8px;">
          الإجمالي: ${Number(inv.amount).toLocaleString()} ج.م
        </div>
        ${inv.notes ? `<p style="margin-top:12px;font-size:12px;color:#666;">ملاحظات: ${inv.notes}</p>` : ""}
      `;
      document.body.appendChild(container);

      const canvas = await html2canvas(container, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
      document.body.removeChild(container);

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = 190;
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      const pageHeight = 277;
      let position = 10;
      let heightLeft = pdfHeight;

      pdf.addImage(imgData, "PNG", 10, position, pdfWidth, pdfHeight);
      heightLeft -= (pageHeight - position);
      while (heightLeft > 0) {
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 10, -(pdfHeight - heightLeft), pdfWidth, pdfHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`فاتورة-${inv.children?.full_name || inv.id.slice(0, 8)}.pdf`);
      toast.success("تم تحميل الفاتورة PDF");
    } catch (e) {
      console.error("PDF export failed:", e);
      toast.error("خطأ في تحميل الفاتورة");
    }
  };

  const renderInvoiceCard = (inv: any, i: number) => {
    const isExpanded = expandedInvoice === inv.id;
    const items = getItemsForInvoice(inv.id);
    const isOverdue = inv.status === "overdue";

    return (
      <motion.div key={inv.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}>
        <Card className={`shadow-card hover:shadow-elevated transition-shadow ${isOverdue ? "border-destructive/40" : inv.status === "paid" ? "border-green-200 dark:border-green-800" : ""}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                  isOverdue ? "bg-red-100 dark:bg-red-900/30" : inv.status === "paid" ? "bg-green-100 dark:bg-green-900/30" : "bg-muted"
                }`}>
                  <Receipt className={`w-5 h-5 ${
                    isOverdue ? "text-red-500" : inv.status === "paid" ? "text-green-500" : "text-muted-foreground"
                  }`} />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-card-foreground">{inv.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {inv.children?.full_name} — {inv.children?.parent_name || "بدون ولي أمر"}
                  </p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Badge variant="outline" className="text-xs">{feeTypeLabel(inv.fee_type)}</Badge>
                    <span className="text-xs text-muted-foreground">استحقاق: {formatDate(inv.due_date)}</span>
                    {inv.paid_at && <span className="text-xs text-green-600">دفع: {formatDate(inv.paid_at)}</span>}
                    {items.length > 0 && (
                      <Badge variant="outline" className="text-xs gap-1">
                        <FileText className="w-3 h-3" /> {items.length} بند
                      </Badge>
                    )}
                    {inv.receipt_url && (
                      <Badge variant="outline" className="text-xs gap-1 cursor-pointer hover:bg-muted" onClick={() => openReceiptPreview(inv.receipt_url)}>
                        <Image className="w-3 h-3" /> إيصال مرفق
                      </Badge>
                    )}
                  </div>
                  {inv.rejection_reason && (
                    <p className="text-[11px] text-destructive mt-1">سبب الرفض: {inv.rejection_reason}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 flex-wrap">
                <p className="text-lg font-bold text-foreground">{Number(inv.amount).toLocaleString()} ج.م</p>
                {statusBadge(inv.status)}
                {(inv.status === "pending" || inv.status === "overdue") && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => markAsPaid(inv.id, "cash")} title="دفع نقدي">
                      <Banknote className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => markAsPaid(inv.id, "transfer")} title="تحويل بنكي">
                      <CreditCard className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
                {isOverdue && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-destructive border-destructive/30 hover:bg-destructive/10"
                    onClick={() => sendAlert(inv)}
                    disabled={sendingAlert === inv.id}
                    title="إرسال تنبيه لولي الأمر"
                  >
                    {sendingAlert === inv.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => handleDownloadPDF(inv)} title="تحميل PDF">
                  <Download className="w-3.5 h-3.5" />
                </Button>
                {items.length > 0 && (
                  <Button size="sm" variant="ghost" onClick={() => setExpandedInvoice(isExpanded ? null : inv.id)}>
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </Button>
                )}
                <DeleteConfirmDialog
                  title="حذف الفاتورة"
                  description="هل أنت متأكد من حذف هذه الفاتورة؟"
                  onConfirm={async () => {
                    await supabase.from("invoices").delete().eq("id", inv.id);
                    toast.success("تم حذف الفاتورة");
                    refetch();
                  }}
                />
              </div>
            </div>

            <AnimatePresence>
              {isExpanded && items.length > 0 && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="mt-3 pt-3 border-t space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground">بنود الفاتورة</p>
                    {items.map((item: any) => (
                      <div key={item.id} className="flex items-center justify-between bg-muted/30 rounded-md px-3 py-2 text-sm">
                        <div className="flex items-center gap-2">
                          {itemTypeIcon(item.item_type)}
                          <span>{item.description}</span>
                          <Badge variant="outline" className="text-[10px]">{itemTypeLabel(item.item_type)}</Badge>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>{Number(item.amount).toLocaleString()} × {item.quantity}</span>
                          <span className="font-bold text-foreground">{Number(item.total || item.amount * item.quantity).toLocaleString()} ج.م</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">المحاسبة والفواتير</h1>
          <p className="text-muted-foreground text-sm mt-1">إدارة الإيرادات والمصروفات</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={mainTab === "expenses" ? handleExportExpensesCSV : handleExportCSV} disabled={mainTab === "expenses" ? !filteredExpenses.length : !filtered.length}>
            <Download className="w-4 h-4 ml-2" /> تصدير CSV
          </Button>
          {mainTab === "expenses" ? (
            <AddExpenseDialog onSuccess={refetchExpenses} />
          ) : (
            <>
              <AddFeeTemplateDialog onSuccess={refetchTemplates} />
              <AddInvoiceDialog children={children || []} feeTemplates={feeTemplates || []} onSuccess={refetch} />
            </>
          )}
        </div>
      </div>

      {/* Overdue Alert Banner */}
      {overdueCount > 0 && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-destructive/50 bg-destructive/5">
            <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-destructive" />
                </div>
                <div>
                  <p className="font-semibold text-destructive">
                    {overdueCount} فاتورة متأخرة بقيمة {totalOverdue.toLocaleString()} ج.م
                  </p>
                  <p className="text-xs text-muted-foreground">يرجى متابعة التحصيل أو إرسال تنبيهات لأولياء الأمور</p>
                </div>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={sendBulkAlerts}
                disabled={sendingBulk}
              >
                {sendingBulk ? <Loader2 className="w-4 h-4 ml-2 animate-spin" /> : <BellRing className="w-4 h-4 ml-2" />}
                تنبيه جماعي
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
        {[
          { label: "إجمالي الإيرادات", value: totalAll, icon: Receipt, bg: "bg-primary/10", iconColor: "text-primary" },
          { label: "معلقة", value: totalPending, icon: Clock, bg: "bg-yellow-100 dark:bg-yellow-900/30", iconColor: "text-yellow-600 dark:text-yellow-400" },
          { label: "مدفوعة", value: totalPaid, icon: CheckCircle, bg: "bg-green-100 dark:bg-green-900/30", iconColor: "text-green-600 dark:text-green-400" },
          { label: "متأخرة", value: totalOverdue, icon: AlertTriangle, bg: "bg-red-100 dark:bg-red-900/30", iconColor: "text-red-600 dark:text-red-400" },
          { label: "المصروفات الشهرية", value: currentMonthExpenses, icon: Minus, bg: "bg-orange-100 dark:bg-orange-900/30", iconColor: "text-orange-600 dark:text-orange-400" },
          { label: "إجمالي المصروفات", value: totalExpenses, icon: DollarSign, bg: "bg-purple-100 dark:bg-purple-900/30", iconColor: "text-purple-600 dark:text-purple-400" },
          { label: "صافي الربح", value: netProfit, icon: TrendingUp, bg: netProfit >= 0 ? "bg-green-100 dark:bg-green-900/30" : "bg-red-100 dark:bg-red-900/30", iconColor: netProfit >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400" },
        ].map((stat) => (
          <motion.div key={stat.label} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}>
            <Card className="shadow-card">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center shrink-0`}>
                  <stat.icon className={`w-5 h-5 ${stat.iconColor}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground truncate">{stat.label}</p>
                  <p className="text-lg font-bold text-foreground">{stat.value.toLocaleString()} ج.م</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Collection Rate */}
      <Card className="shadow-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary" />
              <p className="text-sm font-medium text-foreground">نسبة التحصيل</p>
            </div>
            <p className="text-2xl font-bold text-foreground">{collectionRate}%</p>
          </div>
          <Progress value={collectionRate} className="h-2" />
        </CardContent>
      </Card>

      {/* Charts Section */}
      {allInvoices.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="shadow-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">الإيرادات الشهرية</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyChartData}>
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(value: number) => `${value.toLocaleString()} ج.م`}
                      contentStyle={{ borderRadius: 8, fontSize: 12, direction: "rtl" }}
                    />
                    <Bar dataKey="paid" name="مدفوع" fill={CHART_COLORS.paid} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="pending" name="معلق" fill={CHART_COLORS.pending} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">توزيع الفواتير</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={85}
                      paddingAngle={3}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {pieData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => `${value} فاتورة`} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs: Invoices vs Expenses */}
      <Tabs value={mainTab} onValueChange={setMainTab} dir="rtl">
        <TabsList>
          <TabsTrigger value="invoices" className="gap-1">
            <Receipt className="w-4 h-4" /> الفواتير ({allInvoices.length})
          </TabsTrigger>
          <TabsTrigger value="review" className="gap-1">
            <Eye className="w-4 h-4" /> مراجعة المدفوعات {pendingReviewInvoices.length > 0 && <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">{pendingReviewInvoices.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="subscriptions" className="gap-1">
            <CalendarClock className="w-4 h-4" /> الاشتراكات الشهرية
          </TabsTrigger>
          <TabsTrigger value="expenses" className="gap-1">
            <Minus className="w-4 h-4" /> المصروفات ({allExpenses.length})
          </TabsTrigger>
        </TabsList>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-4 mt-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="بحث..." value={search} onChange={(e) => setSearch(e.target.value)} className="pr-9" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[150px]">
                <Filter className="w-4 h-4 ml-2 text-muted-foreground" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الحالات</SelectItem>
                <SelectItem value="pending">معلقة</SelectItem>
                <SelectItem value="paid">مدفوعة</SelectItem>
                <SelectItem value="overdue">متأخرة</SelectItem>
                <SelectItem value="awaiting_confirmation">بانتظار التأكيد</SelectItem>
                <SelectItem value="under_review">قيد المراجعة</SelectItem>
                <SelectItem value="rejected">مرفوض</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الأنواع</SelectItem>
                <SelectItem value="monthly">شهرية</SelectItem>
                <SelectItem value="additional">إضافية</SelectItem>
              </SelectContent>
            </Select>
            {(classes || []).length > 0 && (
              <Select value={classFilter} onValueChange={setClassFilter}>
                <SelectTrigger className="w-full sm:w-[150px]">
                  <SelectValue placeholder="الفصل" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">جميع الفصول</SelectItem>
                  {(classes || []).map((c: any) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Invoices List */}
          {isLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
          ) : filtered.length === 0 ? (
            <Card className="shadow-card">
              <CardContent className="py-12 text-center">
                <Receipt className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-muted-foreground">لا توجد فواتير</p>
              </CardContent>
            </Card>
          ) : (
            <Tabs defaultValue="all" dir="rtl">
              <TabsList>
                <TabsTrigger value="all">الكل ({filtered.length})</TabsTrigger>
                <TabsTrigger value="monthly">شهرية ({monthlyInvoices.length})</TabsTrigger>
                <TabsTrigger value="additional">إضافية ({additionalInvoices.length})</TabsTrigger>
              </TabsList>
              <TabsContent value="all" className="space-y-3 mt-4">
                {filtered.map((inv: any, i: number) => renderInvoiceCard(inv, i))}
              </TabsContent>
              <TabsContent value="monthly" className="space-y-3 mt-4">
                {monthlyInvoices.map((inv: any, i: number) => renderInvoiceCard(inv, i))}
              </TabsContent>
              <TabsContent value="additional" className="space-y-3 mt-4">
                {additionalInvoices.length === 0 ? (
                  <Card className="shadow-card"><CardContent className="py-8 text-center text-muted-foreground">لا توجد فواتير إضافية</CardContent></Card>
                ) : additionalInvoices.map((inv: any, i: number) => renderInvoiceCard(inv, i))}
              </TabsContent>
            </Tabs>
          )}

          {/* Fee Templates */}
          {(feeTemplates || []).length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold text-foreground">قوالب الرسوم</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {(feeTemplates || []).map((t: any) => (
                  <Card key={t.id} className="shadow-card">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-card-foreground">{t.name}</p>
                        <p className="text-xs text-muted-foreground">{feeTypeLabel(t.fee_type)}</p>
                        {t.description && <p className="text-xs text-muted-foreground mt-1">{t.description}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-primary">{Number(t.amount).toLocaleString()} ج.م</p>
                        <DeleteConfirmDialog
                          title="حذف القالب"
                          description="هل أنت متأكد من حذف قالب الرسوم؟"
                          onConfirm={async () => {
                            await supabase.from("fee_templates").delete().eq("id", t.id);
                            toast.success("تم الحذف");
                            refetchTemplates();
                          }}
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Review Tab */}
        <TabsContent value="review" className="space-y-4 mt-4">
          {pendingReviewInvoices.length === 0 ? (
            <Card className="shadow-card">
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <p className="text-muted-foreground">لا توجد مدفوعات تحتاج مراجعة</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {pendingReviewInvoices.map((inv: any, i: number) => (
                <motion.div key={inv.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                  <Card className="shadow-card border-blue-200 dark:border-blue-800">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                            inv.status === "awaiting_confirmation" ? "bg-orange-100 dark:bg-orange-900/30" : "bg-blue-100 dark:bg-blue-900/30"
                          }`}>
                            {inv.status === "awaiting_confirmation" ? (
                              <Banknote className="w-5 h-5 text-orange-500" />
                            ) : (
                              <Image className="w-5 h-5 text-blue-500" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-card-foreground">{inv.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {inv.children?.full_name} — {inv.children?.parent_name || "بدون ولي أمر"}
                            </p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              {statusBadge(inv.status)}
                              <span className="text-xs text-muted-foreground">
                                {inv.payment_method === "cash" ? "كاش" : "تحويل بنكي"}
                              </span>
                              <span className="text-xs text-muted-foreground">استحقاق: {formatDate(inv.due_date)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 flex-wrap">
                          <p className="text-lg font-bold text-foreground">{Number(inv.amount).toLocaleString()} ج.م</p>
                          {inv.receipt_url && (
                            <Button size="sm" variant="outline" onClick={() => openReceiptPreview(inv.receipt_url)} title="عرض الإيصال">
                              <Eye className="w-3.5 h-3.5 ml-1" /> الإيصال
                            </Button>
                          )}
                          <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => approvePayment(inv)} disabled={reviewSubmitting}>
                            {reviewSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5 ml-1" />}
                            تأكيد
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => { setReviewingInvoice(inv); setRejectionReason(""); }} disabled={reviewSubmitting}>
                            <XCircle className="w-3.5 h-3.5 ml-1" /> رفض
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="subscriptions" className="space-y-4 mt-4">
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="shadow-card">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">أطفال مشتركين</p>
                  <p className="text-lg font-bold text-foreground">{childrenWithFees.length}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="shadow-card">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
                  <Receipt className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">فواتير الشهر</p>
                  <p className="text-lg font-bold text-foreground">{currentMonthInvoices.length}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="shadow-card">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center shrink-0">
                  <UserCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">مدفوع هذا الشهر</p>
                  <p className="text-lg font-bold text-foreground">{paidThisMonth} / {currentMonthInvoices.length}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="shadow-card">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center shrink-0">
                  <Percent className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">نسبة السداد الشهرية</p>
                  <p className="text-lg font-bold text-foreground">{monthlyCollectionRate}%</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Generate Button */}
          <Card className="shadow-card">
            <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="font-medium text-foreground">توليد فواتير الشهر الحالي</p>
                <p className="text-xs text-muted-foreground">إصدار فواتير تلقائية لجميع الأطفال المشتركين (مع حساب الاشتراك الجزئي Pro-rata للمسجلين حديثاً)</p>
              </div>
              <Button onClick={generateMonthlyInvoices} disabled={generatingInvoices || childrenWithFees.length === 0} className="gradient-hero border-0">
                {generatingInvoices ? <Loader2 className="w-4 h-4 ml-2 animate-spin" /> : <CalendarClock className="w-4 h-4 ml-2" />}
                توليد الفواتير
              </Button>
            </CardContent>
          </Card>

          {/* Children subscriptions list */}
          {childrenWithFees.length === 0 ? (
            <Card className="shadow-card">
              <CardContent className="py-12 text-center">
                <CalendarClock className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-muted-foreground">لا يوجد أطفال بقيمة اشتراك شهري</p>
                <p className="text-xs text-muted-foreground mt-1">قم بتعيين قيمة الاشتراك الشهري من صفحة إدارة الأطفال</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {childrenWithFees.map((child: any) => {
                const childInvoice = currentMonthInvoices.find((i: any) => i.child_id === child.id);
                const status = childInvoice?.status || "no_invoice";
                return (
                  <Card key={child.id} className={`shadow-card ${status === "paid" ? "border-green-200 dark:border-green-800" : status === "overdue" ? "border-destructive/40" : ""}`}>
                    <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-foreground">{child.full_name}</p>
                        <p className="text-xs text-muted-foreground">{child.parent_name || "بدون ولي أمر"}</p>
                        {child.enrollment_date && (
                          <p className="text-xs text-muted-foreground mt-1">تاريخ التسجيل: {formatDate(child.enrollment_date)}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="font-bold text-foreground">{Number(child.monthly_fee).toLocaleString()} ج.م/شهر</p>
                        {status === "paid" && <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"><CheckCircle className="w-3 h-3 ml-1" />مدفوع</Badge>}
                        {status === "pending" && <Badge variant="secondary" className="gap-1"><Clock className="w-3 h-3" />معلق</Badge>}
                        {status === "overdue" && <Badge variant="destructive" className="gap-1"><AlertTriangle className="w-3 h-3" />متأخر</Badge>}
                        {status === "no_invoice" && <Badge variant="outline">لم تُصدر فاتورة</Badge>}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="space-y-4 mt-4">
          {/* Expense Category Filter */}
          <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
            <Select value={expenseCategoryFilter} onValueChange={setExpenseCategoryFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <Filter className="w-4 h-4 ml-2 text-muted-foreground" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الفئات</SelectItem>
                {EXPENSE_CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Expense Category Summary */}
          {allExpenses.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(
                allExpenses.reduce((acc: Record<string, number>, e: any) => {
                  acc[e.category] = (acc[e.category] || 0) + Number(e.amount);
                  return acc;
                }, {})
              )
                .sort((a, b) => b[1] - a[1])
                .slice(0, 4)
                .map(([cat, total]) => {
                  const config = CATEGORY_CONFIG[cat] || CATEGORY_CONFIG.other;
                  const Icon = config.icon;
                  return (
                    <Card key={cat} className="shadow-card">
                      <CardContent className="p-3 flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${config.color.split(" ").slice(1).join(" ")}`}>
                          <Icon className={`w-4 h-4 ${config.color.split(" ")[0]}`} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs text-muted-foreground">{config.label}</p>
                          <p className="text-sm font-bold text-foreground">{(total as number).toLocaleString()} ج.م</p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
            </div>
          )}

          {/* Expenses List */}
          {expensesLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
          ) : filteredExpenses.length === 0 ? (
            <Card className="shadow-card">
              <CardContent className="py-12 text-center">
                <Minus className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-muted-foreground">لا توجد مصروفات</p>
                <p className="text-xs text-muted-foreground mt-1">اضغط "إضافة مصروف" لتسجيل مصروف جديد</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredExpenses.map((expense: any, i: number) => {
                const config = CATEGORY_CONFIG[expense.category] || CATEGORY_CONFIG.other;
                const Icon = config.icon;
                return (
                  <motion.div key={expense.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}>
                    <Card className="shadow-card hover:shadow-elevated transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${config.color.split(" ").slice(1).join(" ")}`}>
                              <Icon className={`w-5 h-5 ${config.color.split(" ")[0]}`} />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-card-foreground">{expense.title}</p>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <Badge variant="outline" className="text-xs">{config.label}</Badge>
                                <span className="text-xs text-muted-foreground">{formatDate(expense.expense_date)}</span>
                                {expense.is_recurring && (
                                  <Badge variant="secondary" className="text-xs gap-1">
                                    <RefreshCw className="w-3 h-3" /> متكرر
                                  </Badge>
                                )}
                                {expense.payment_method && (
                                  <span className="text-xs text-muted-foreground">
                                    {expense.payment_method === "cash" ? "نقدي" : expense.payment_method === "transfer" ? "تحويل" : expense.payment_method === "card" ? "بطاقة" : "شيك"}
                                  </span>
                                )}
                              </div>
                              {expense.notes && <p className="text-xs text-muted-foreground mt-1">{expense.notes}</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <p className="text-lg font-bold text-foreground">{Number(expense.amount).toLocaleString()} ج.م</p>
                            <DeleteConfirmDialog
                              title="حذف المصروف"
                              description={`هل أنت متأكد من حذف "${expense.title}"؟`}
                              onConfirm={() => deleteExpense(expense.id)}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Rejection Dialog */}
      <Dialog open={!!reviewingInvoice} onOpenChange={(open) => { if (!open) { setReviewingInvoice(null); setRejectionReason(""); } }}>
        <DialogContent dir="rtl" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>رفض الدفع</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              رفض دفع فاتورة "{reviewingInvoice?.title}" — {reviewingInvoice?.amount?.toLocaleString()} ج.م
            </p>
            <p className="text-sm font-medium">سبب الرفض</p>
            <Textarea
              placeholder="مثال: صورة الإيصال غير واضحة..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter className="flex-row-reverse gap-2">
            <Button variant="destructive" onClick={rejectPayment} disabled={reviewSubmitting || !rejectionReason.trim()}>
              {reviewSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "تأكيد الرفض"}
            </Button>
            <Button variant="outline" onClick={() => { setReviewingInvoice(null); setRejectionReason(""); }}>إلغاء</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Preview Dialog */}
      <Dialog open={!!receiptPreviewUrl} onOpenChange={(open) => { if (!open) setReceiptPreviewUrl(null); }}>
        <DialogContent dir="rtl" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>صورة الإيصال</DialogTitle>
          </DialogHeader>
          {receiptPreviewUrl && (
            <div className="flex justify-center">
              <img src={receiptPreviewUrl} alt="إيصال الدفع" className="max-w-full max-h-[500px] object-contain rounded-lg border" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Billing;
